/**
 * Lista de usuários da gestão de acesso: busca, filtros, ordenação, paginação e
 * apoio visual (iniciais, cor do avatar, realce da busca).
 *
 * Funções PURAS, sem React nem DOM — as duas cascas (desktop e mobile) consomem
 * a mesma lógica, e ela é testável sem renderizar nada. Antes a tela era uma
 * tabela crua com todos os usuários, sem como achar ninguém.
 */
import type { UsuarioPublico } from "@caint/shared";

/** Fonte ÚNICA dos rótulos de perfil (era duplicada nas duas telas). */
export const PAPEL_ROTULO: Record<string, string> = {
  gqa: "Avaliador GQA",
  ggo: "Avaliador GGO",
  ncig: "Avaliador NCIG",
  audin: "Supervisor (AUDIN)",
  coord: "Coordenação CAINT",
  presidente: "Presidente CAINT",
  admin: "Administrador",
  visualizador: "Visualizador (só leitura)",
  unidade: "Unidade (Plano de Ação)",
};

/** Ordem de exibição dos perfis em selects e filtros. */
export const PAPEIS_ORDEM: readonly string[] = [
  "gqa",
  "ggo",
  "ncig",
  "audin",
  "coord",
  "presidente",
  "admin",
  "visualizador",
  "unidade",
];

export type GrupoPapel = "avaliacao" | "supervisao" | "gestao" | "leitura" | "unidade";

/** Família do perfil — agrupa o select e dá a cor do selo. */
export const PAPEL_GRUPO: Record<string, GrupoPapel> = {
  gqa: "avaliacao",
  ggo: "avaliacao",
  ncig: "avaliacao",
  audin: "supervisao",
  coord: "gestao",
  presidente: "gestao",
  admin: "gestao",
  visualizador: "leitura",
  unidade: "unidade",
};

export const GRUPO_ROTULO: Record<GrupoPapel, string> = {
  avaliacao: "Avaliação",
  supervisao: "Supervisão",
  gestao: "Gestão",
  leitura: "Somente leitura",
  unidade: "Unidade",
};

export function rotuloPapel(papel: string): string {
  return PAPEL_ROTULO[papel] ?? papel;
}

/** Rótulo enxuto para o selo da lista — o completo não cabe numa pílula. */
const PAPEL_ROTULO_CURTO: Record<string, string> = {
  audin: "Supervisor AUDIN",
  coord: "Coordenação",
  presidente: "Presidente",
  visualizador: "Visualizador",
  unidade: "Unidade",
};

export function rotuloPapelCurto(papel: string): string {
  return PAPEL_ROTULO_CURTO[papel] ?? rotuloPapel(papel);
}

export const EIXOS = ["IMGO", "IMQA", "IMGR"] as const;

export type StatusFiltro = "todos" | "ativos" | "inativos" | "pendentes";

export interface FiltrosUsuarios {
  busca: string;
  status: StatusFiltro;
  /** Vazio ⇒ todos os perfis. */
  papeis: readonly string[];
  /** "" ⇒ todos · "IMGO" | "IMQA" | "IMGR" · "sem" ⇒ só quem não tem eixo. */
  eixo: string;
  /** "" ⇒ todas. */
  unidadeId: string;
}

export const FILTROS_VAZIOS: FiltrosUsuarios = {
  busca: "",
  status: "todos",
  papeis: [],
  eixo: "",
  unidadeId: "",
};

/** Quantos filtros (fora a busca) estão ativos — o contador do "Limpar filtros". */
export function contarFiltrosAtivos(f: FiltrosUsuarios): number {
  return (
    (f.status !== "todos" ? 1 : 0) + (f.papeis.length > 0 ? 1 : 0) + (f.eixo ? 1 : 0) + (f.unidadeId ? 1 : 0)
  );
}

/** Há busca digitada OU algum filtro ligado. */
export function temFiltro(f: FiltrosUsuarios): boolean {
  return f.busca.trim() !== "" || contarFiltrosAtivos(f) > 0;
}

/** Marcas combinantes (acentos soltos depois da decomposição NFD). */
const DIACRITICOS = /\p{M}/gu;

/** Minúsculas, sem acento e sem espaço nas pontas ("João " casa com "joao"). */
export function normalizar(texto: string): string {
  return texto.normalize("NFD").replace(DIACRITICOS, "").toLowerCase().trim();
}

/**
 * Conta ATIVA com senha provisória vigente: a pessoa ainda não entrou desde a
 * criação/redefinição. `acessoPendente` é opcional na API — ausente conta como
 * "não pendente" (servidor antigo não pode pintar todo mundo de pendente).
 */
export function acessoPendente(u: UsuarioPublico): boolean {
  return u.ativo && u.acessoPendente === true;
}

export type StatusUsuario = "ativo" | "inativo" | "pendente";

export function statusUsuario(u: UsuarioPublico): StatusUsuario {
  if (!u.ativo) {
    return "inativo";
  }
  return acessoPendente(u) ? "pendente" : "ativo";
}

export interface OpcoesLista {
  /** Resolve o nome da unidade — injetado para a lib não depender do cadastro. */
  nomeUnidade?: (unidadeId: string) => string;
  /** Rótulo do cargo na unidade ("Diretor(a)"). */
  nomeCargo?: (cargo: string) => string;
}

/**
 * Palavras de status aceitas na busca. Casam só por palavra INTEIRA: "ativo" é
 * pedaço de "inativo" (e "ati" de "Fátima"), então casar por trecho devolveria
 * a lista quase toda.
 */
const PALAVRA_STATUS: Record<string, StatusUsuario> = {
  ativo: "ativo",
  ativos: "ativo",
  ativa: "ativo",
  ativas: "ativo",
  inativo: "inativo",
  inativos: "inativo",
  inativa: "inativo",
  inativas: "inativo",
  pendente: "pendente",
  pendentes: "pendente",
};

function textoBuscavel(u: UsuarioPublico, o?: OpcoesLista): string {
  const partes = [u.nome, u.matricula, rotuloPapel(u.papel), u.papel, u.eixo ?? ""];
  if (u.cargoUnidade) {
    partes.push(o?.nomeCargo ? o.nomeCargo(u.cargoUnidade) : u.cargoUnidade);
  }
  if (u.unidadeId && o?.nomeUnidade) {
    partes.push(o.nomeUnidade(u.unidadeId));
  }
  return normalizar(partes.join(" "));
}

function casaStatus(u: UsuarioPublico, alvo: StatusUsuario): boolean {
  // "ativo" inclui quem está com acesso pendente: a conta está ativa.
  return alvo === "ativo" ? u.ativo : statusUsuario(u) === alvo;
}

function passaStatus(u: UsuarioPublico, status: StatusFiltro): boolean {
  switch (status) {
    case "ativos":
      return u.ativo;
    case "inativos":
      return !u.ativo;
    case "pendentes":
      return acessoPendente(u);
    default:
      return true;
  }
}

function passaEixo(u: UsuarioPublico, eixo: string): boolean {
  if (!eixo) {
    return true;
  }
  return eixo === "sem" ? !u.eixo : u.eixo === eixo;
}

function passaFiltros(u: UsuarioPublico, f: FiltrosUsuarios): boolean {
  return (
    passaStatus(u, f.status) &&
    (f.papeis.length === 0 || f.papeis.includes(u.papel)) &&
    passaEixo(u, f.eixo) &&
    (!f.unidadeId || u.unidadeId === f.unidadeId)
  );
}

/**
 * Busca por TERMOS: cada palavra digitada precisa aparecer em algum campo (E
 * lógico), sem acento nem caixa. Campos: nome, e-mail, perfil (rótulo e chave),
 * eixo, cargo e unidade; "ativo"/"inativo"/"pendente" casam com o status.
 * Ex.: "gqa inativo", "itapecuru diretor", "ana imqa".
 */
export function filtrarUsuarios(
  lista: readonly UsuarioPublico[],
  f: FiltrosUsuarios,
  o?: OpcoesLista,
): UsuarioPublico[] {
  const termos = normalizar(f.busca).split(/\s+/).filter(Boolean);
  return lista.filter((u) => {
    if (!passaFiltros(u, f)) {
      return false;
    }
    if (termos.length === 0) {
      return true;
    }
    const texto = textoBuscavel(u, o);
    return termos.every((t) => {
      const status = PALAVRA_STATUS[t];
      return texto.includes(t) || (status !== undefined && casaStatus(u, status));
    });
  });
}

export type ColunaOrdem = "nome" | "papel" | "eixo" | "status";

export interface Ordem {
  coluna: ColunaOrdem;
  dir: "asc" | "desc";
}

export const ORDEM_PADRAO: Ordem = { coluna: "nome", dir: "asc" };

const ORDEM_STATUS: Record<StatusUsuario, number> = { ativo: 0, pendente: 1, inativo: 2 };

function compararTexto(a: string, b: string): number {
  return a.localeCompare(b, "pt-BR", { sensitivity: "base" });
}

/**
 * Data de criação da conta no fuso de quem está olhando ("12/03/2026"), ou ""
 * quando a API não informa (servidor antigo) ou a data é inválida.
 */
export function criadaEm(u: UsuarioPublico): string {
  const t = u.criadoEm ? Date.parse(u.criadoEm) : Number.NaN;
  return Number.isNaN(t) ? "" : new Date(t).toLocaleDateString("pt-BR");
}

/** Valor de ordenação da coluna; `null` ⇒ vazio (vai para o fim nas duas direções). */
function chaveOrdem(u: UsuarioPublico, coluna: ColunaOrdem): ChaveOrdem {
  switch (coluna) {
    case "nome":
      return u.nome;
    case "papel": {
      const i = PAPEIS_ORDEM.indexOf(u.papel);
      return i === -1 ? PAPEIS_ORDEM.length : i;
    }
    case "eixo":
      return u.eixo || null;
    case "status":
      return ORDEM_STATUS[statusUsuario(u)];
  }
}

/**
 * Ordena sem mutar a entrada. Vazios (conta sem eixo) ficam por ÚLTIMO nas
 * duas direções — inverter a ordem não pode jogar os "—" para o topo. Empate
 * desfaz por nome (sempre crescente) e depois por id, o que torna a ordem
 * determinística entre recargas.
 */
export function ordenarUsuarios(lista: readonly UsuarioPublico[], ordem: Ordem): UsuarioPublico[] {
  const sinal = ordem.dir === "asc" ? 1 : -1;
  return [...lista].sort(
    (a, b) =>
      compararChaves(chaveOrdem(a, ordem.coluna), chaveOrdem(b, ordem.coluna), sinal) ||
      compararTexto(a.nome, b.nome) ||
      a.id.localeCompare(b.id),
  );
}

type ChaveOrdem = string | number | null;

/** Compara duas chaves na direção pedida; vazio perde sempre (não sofre o `sinal`). */
function compararChaves(va: ChaveOrdem, vb: ChaveOrdem, sinal: number): number {
  if (va === null || vb === null) {
    if (va === vb) {
      return 0;
    }
    return va === null ? 1 : -1;
  }
  const c =
    typeof va === "number" && typeof vb === "number" ? va - vb : compararTexto(String(va), String(vb));
  return c * sinal;
}

/** Clique no cabeçalho: a mesma coluna inverte; outra coluna começa crescente. */
export function alternarOrdem(atual: Ordem, coluna: ColunaOrdem): Ordem {
  if (atual.coluna === coluna) {
    return { coluna, dir: atual.dir === "asc" ? "desc" : "asc" };
  }
  return { coluna, dir: "asc" };
}

export interface ContagemUsuarios {
  total: number;
  ativos: number;
  inativos: number;
  pendentes: number;
  porPapel: Record<string, number>;
  /** Chaves IMGO/IMQA/IMGR/"sem". */
  porEixo: Record<string, number>;
  /** unidadeId → nº de contas (só contas com unidade). */
  porUnidade: Record<string, number>;
}

/** Contagens da lista INTEIRA (não a filtrada): alimentam as abas e os filtros. */
export function contarUsuarios(lista: readonly UsuarioPublico[]): ContagemUsuarios {
  const c: ContagemUsuarios = {
    total: lista.length,
    ativos: 0,
    inativos: 0,
    pendentes: 0,
    porPapel: Object.fromEntries(PAPEIS_ORDEM.map((p) => [p, 0])),
    porEixo: { IMGO: 0, IMQA: 0, IMGR: 0, sem: 0 },
    porUnidade: {},
  };
  for (const u of lista) {
    if (u.ativo) {
      c.ativos += 1;
    } else {
      c.inativos += 1;
    }
    if (acessoPendente(u)) {
      c.pendentes += 1;
    }
    c.porPapel[u.papel] = (c.porPapel[u.papel] ?? 0) + 1;
    const eixo = u.eixo || "sem";
    c.porEixo[eixo] = (c.porEixo[eixo] ?? 0) + 1;
    if (u.unidadeId) {
      c.porUnidade[u.unidadeId] = (c.porUnidade[u.unidadeId] ?? 0) + 1;
    }
  }
  return c;
}

export interface Pagina<T> {
  itens: T[];
  /** 1-based, já corrigida para o intervalo válido. */
  pagina: number;
  totalPaginas: number;
  /** Posição (1-based) do primeiro e do último item — "Mostrando 26–50 de 87". */
  inicio: number;
  fim: number;
  total: number;
}

/**
 * Recorta uma página. `pagina` fora do intervalo é CORRIGIDA em vez de devolver
 * vazio: quando o filtro encolhe a lista, a tela não pode ficar numa página que
 * deixou de existir.
 */
export function paginar<T>(lista: readonly T[], pagina: number, tamanho: number): Pagina<T> {
  const total = lista.length;
  const porPagina = Math.max(1, Math.floor(tamanho) || 1);
  const totalPaginas = Math.max(1, Math.ceil(total / porPagina));
  const atual = Math.min(totalPaginas, Math.max(1, Math.floor(pagina) || 1));
  const de = (atual - 1) * porPagina;
  const itens = lista.slice(de, de + porPagina);
  return {
    itens,
    pagina: atual,
    totalPaginas,
    inicio: total === 0 ? 0 : de + 1,
    fim: de + itens.length,
    total,
  };
}

const PREPOSICOES = new Set(["de", "da", "do", "dos", "das", "e"]);

function primeiraLetra(palavra: string): string {
  // Array.from respeita pares substitutos (não corta um caractere ao meio).
  return (Array.from(palavra)[0] ?? "").toLocaleUpperCase("pt-BR");
}

/** "Ana Beatriz Sousa" → "AS"; "Maria de Lourdes" → "ML"; "Madonna" → "MA"; "" → "?". */
export function iniciais(nome: string): string {
  const palavras = nome.trim().split(/\s+/).filter(Boolean);
  const fortes = palavras.filter((p) => !PREPOSICOES.has(p.toLowerCase()));
  const base = fortes.length > 0 ? fortes : palavras;
  const primeira = base[0];
  if (!primeira) {
    return "?";
  }
  const ultima = base[base.length - 1];
  if (base.length === 1 || !ultima) {
    return Array.from(primeira).slice(0, 2).join("").toLocaleUpperCase("pt-BR");
  }
  return primeiraLetra(primeira) + primeiraLetra(ultima);
}

/** Índice determinístico 0..n-1 a partir de uma semente (o id) — cor de avatar estável. */
export function indiceCor(semente: string, n: number): number {
  if (n <= 1) {
    return 0;
  }
  let h = 5381;
  for (let i = 0; i < semente.length; i++) {
    h = (h * 33 + semente.charCodeAt(i)) >>> 0;
  }
  return h % n;
}

export interface TrechoDestaque {
  texto: string;
  marca: boolean;
}

/**
 * Parte `texto` em trechos, marcando onde os termos da busca aparecem (sem
 * acento/caixa), mas devolvendo o texto ORIGINAL — "joao" marca "João".
 *
 * Tirar o acento muda o tamanho da string, então os índices do texto
 * normalizado não servem no original: cada caractere normalizado guarda de qual
 * posição original veio.
 */
export function destacar(texto: string, busca: string): TrechoDestaque[] {
  const termos = normalizar(busca).split(/\s+/).filter(Boolean);
  if (termos.length === 0 || texto === "") {
    return [{ texto, marca: false }];
  }
  const { plano, origem, semSaida } = planificar(texto);
  const marcado = new Array<boolean>(texto.length).fill(false);
  for (const termo of termos) {
    for (let de = plano.indexOf(termo); de !== -1; de = plano.indexOf(termo, de + 1)) {
      for (const i of origem.slice(de, de + termo.length)) {
        marcado[i] = true;
      }
    }
  }
  // Acento solto (texto já decomposto) some na normalização: acompanha a letra
  // anterior para o realce não quebrar no meio de "João".
  for (let i = 1; i < texto.length; i++) {
    if (semSaida[i] && marcado[i - 1]) {
      marcado[i] = true;
    }
  }
  return agrupar(texto, marcado);
}

/** Versão sem acento/caixa de `texto` + de qual posição original veio cada caractere. */
function planificar(texto: string): { plano: string; origem: number[]; semSaida: boolean[] } {
  let plano = "";
  const origem: number[] = [];
  const semSaida: boolean[] = [];
  for (let i = 0; i < texto.length; i++) {
    const n = (texto[i] ?? "").normalize("NFD").replace(DIACRITICOS, "").toLowerCase();
    semSaida.push(n.length === 0);
    for (let k = 0; k < n.length; k++) {
      origem.push(i);
    }
    plano += n;
  }
  return { plano, origem, semSaida };
}

/** Junta caracteres vizinhos com a mesma marca em trechos. */
function agrupar(texto: string, marcado: readonly boolean[]): TrechoDestaque[] {
  const trechos: TrechoDestaque[] = [];
  for (let i = 0; i < texto.length; i++) {
    const marca = marcado[i] === true;
    const ultimo = trechos[trechos.length - 1];
    if (ultimo && ultimo.marca === marca) {
      ultimo.texto += texto[i];
    } else {
      trechos.push({ texto: texto[i] ?? "", marca });
    }
  }
  return trechos;
}
